import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { ReactSVG } from 'react-svg'


import { useAuth, useAuthFlow } from '@features/auth'
import useClearCartModal from '@features/cart/hooks/useClearCart'
import { useCartStore } from '@features/cart/model/useCartStore'
import { useAddressStore } from '@features/shipping-methods'

import useDeliveryModal from '@widgets/modal-delivery/ui/useDeliveryModal'



import { CART_FEATURES, URL_CART, URL_PAGE_CATALOG } from '@shared/contants'
import { Product } from '@shared/generated'
import { useZonesFromAddress } from '@shared/hooks/useZonesFromAddress'
import { cn } from '@shared/lib/cn'
import Button from '@shared/ui/button'
import Wrapper from '@shared/ui/wrapper'
import { formatPrice } from '@shared/uttils'
import { formatGramsToHumanWeight } from '@shared/uttils/formatGramsToHumanWeight'
import getNoun from '@shared/uttils/getNoun'

import CartListPreview from './list'

import styles from './styles.module.scss'

function SkeletonLoading () {
  return (
    <SkeletonTheme>
      <Skeleton
        count={3}
        width={'100%'}
        height={80}
        style={{
          borderRadius: 6,
          marginBottom: 20,
        }}
      />
    </SkeletonTheme>
  )
}

export default function CartPreview () {
  const { t: tca } = useTranslation('cart')
  const { t: tc } = useTranslation('common')
  const { t: ta } = useTranslation('auth')
  const { t: tp } = useTranslation('product')
  const { user } = useAuth()
  const {
    getCart,
    getTotalQuantity,
    isSyncing,
    cartInfoData: cartTotal,
    cartInfoLoading: loading,
  } = useCartStore()
  const { openClearCartModal } = useClearCartModal()
  const cart = getCart()
  const {
    minOrderCost,
    isPickup,
    freeSlotDeliveryCostFrom,
    deliveryCost,
    loading: zonesLoading,
  } = useZonesFromAddress()
  const authFlow = useAuthFlow()
  const { openDeliveryModal } = useDeliveryModal()
  const [ showDeliveryList, setShowDeliveryList ] = useState(false)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const [ hasScroll, setHasScroll ] = useState(false)
  const { selectedAddress } = useAddressStore()

  const total = cartTotal?.totals?.total as number

  const HAS_ITEMS = useMemo(() => {
    return !!cart?.items.length
  }, [ cart ])

  const TOTAL = useMemo(() => {
    return getTotalQuantity()
  }, [ cart ])

  const LOADING = useMemo(() => {
    return (loading && !cartTotal) || isSyncing
  }, [ loading, isSyncing, cartTotal ])

  const productsData = useMemo(() => {
    return cartTotal?.products?.map((p) => p?.info)
  }, [ cartTotal ])

  useEffect(() => {
    if (scrollAreaRef.current) {
      const hasVerticalScroll =
        scrollAreaRef.current.scrollHeight > scrollAreaRef.current.clientHeight
      setHasScroll(hasVerticalScroll)
    }
  }, [ productsData ])

  return (
    <Wrapper
      className={cn(styles.root, {
        [styles.products]: HAS_ITEMS,
        [styles.empty]: !HAS_ITEMS,
      })}
    >
      {!HAS_ITEMS && (
        <div className={styles.root__top}>
          {!selectedAddress ? (
            <h2 className={styles.root__title}>{tca('preview.title')}</h2>
          ) : (
            <h2 className={styles.root__title}>
              {tca('preview.titleSelectedAddress')}
            </h2>
          )}
          {!selectedAddress ? (
            <>
              <p className={styles.root__desc}>{tca('preview.desc')}</p>
              <Button size="large" onClick={() => openDeliveryModal()}>
                {tc('address.choose')}
              </Button>
            </>
          ) : (
            <>
              <p className={styles.root__desc}>
                {tca('preview.useSearchOrCatalog')}
              </p>
              <Button size="large" to={URL_PAGE_CATALOG}>
                {tc('button.catalog')}
              </Button>
            </>
          )}
        </div>
      )}

      {HAS_ITEMS && (
        <>
          {!LOADING && (
            <div className={styles.root__top}>
              <span className={styles.root__count}>
                {TOTAL}{' '}
                {getNoun(
                  TOTAL,
                  tp('product.1'),
                  tp('product.2'),
                  tp('product.3'),
                )}
              </span>
              <span className={styles.root__head}>
                {!!cartTotal?.totalWeight && (
                  <p className={styles.root__weight}>
                    ~{' '}
                    {formatGramsToHumanWeight({
                      weight: Number(cartTotal?.totalWeight),
                      t: tc,
                    })}
                  </p>
                )}
                <button
                  className={cn('reset-button', styles.root__clear)}
                  onClick={openClearCartModal}
                >
                  {tca('preview.clear_button')}
                </button>
              </span>
            </div>
          )}

          <div
            ref={scrollAreaRef}
            className={cn(styles.scrollArea, { [styles.hasScroll]: hasScroll })}
          >
            {!LOADING && productsData && productsData.length > 0 ? (
              <CartListPreview data={productsData as Product[]} />
            ) : (
              <SkeletonLoading />
            )}
          </div>
        </>
      )}

      {!HAS_ITEMS && (
        <div className={styles.root__bottom}>
          <div className={styles.root__list}>
            {CART_FEATURES.map((item) => (
              <div key={item.key} className={styles.root__item}>
                <span className={styles.root__icon}>
                  <img
                    src={`/icons/cart/features/${item.key}.svg`}
                    alt=""
                    fetchPriority="low"
                    loading="lazy"
                  />
                </span>
                {tca(`preview.features.${item.key}`)}
              </div>
            ))}
          </div>

          {!user?.first_name && (
            <Button size="large" theme="secondary" onClick={authFlow}>
              {ta('button.auth')}
            </Button>
          )}
        </div>
      )}

      {HAS_ITEMS && cartTotal && (
        <div className={styles.bottomGroup}>
          <div className={styles.root__total}>
            <div className={styles['root__total-row']}>
              <h3 className={styles['root__total-title']}>
                {tca('total.total')}:{' '}
              </h3>
              <b className={styles['root__total-price']}>
                {formatPrice(cartTotal?.totals?.total as number)}
              </b>
            </div>

            {!!cartTotal?.totals?.sale && (
              <div className={styles['root__total-row']}>
                <h3 className={styles['root__total-title']}>
                  {tca('total.discount')}:{' '}
                </h3>
                <span className={styles['root__total-sale']}>
                  -{formatPrice(cartTotal.totals.sale)}
                </span>
              </div>
            )}
          </div>
          <div className={styles['root__buttons']}>
            {LOADING || zonesLoading ? (
              <SkeletonTheme>
                <Skeleton
                  count={1}
                  width="100%"
                  height={56}
                  style={{
                    borderRadius: 12,
                  }}
                />
              </SkeletonTheme>
            ) : !isPickup() ? (
              total < minOrderCost ? (
                <Button disabled size="large">
                  {tca('preview.min_order_message', {
                    minOrderCost: formatPrice(minOrderCost),
                    amount: formatPrice(minOrderCost - total),
                  })}
                </Button>
              ) : (
                <Button size="large" to={URL_CART}>
                  {tca('preview.go_to_cart')}
                </Button>
              )
            ) : (
              <Button size="large" to={URL_CART}>
                {tca('preview.go_to_cart')}
              </Button>
            )}
            {LOADING || zonesLoading ? (
              <SkeletonTheme>
                <Skeleton
                  count={1}
                  width="100%"
                  height={56}
                  style={{
                    borderRadius: 12,
                  }}
                />
              </SkeletonTheme>
            ) : !isPickup() ? (
              <div
                className={styles['root__delivery-cost']}
                onClick={() => setShowDeliveryList((prev) => !prev)}
              >
                <ReactSVG
                  className={styles['root__delivery-cost__icon']}
                  src="/icons/moped.svg"
                  wrapper="span"
                />
                <span className={styles['root__delivery-cost__text']}>
                  {total < freeSlotDeliveryCostFrom
                    ? tca('delivery.amount_to_free', {
                      amount: formatPrice(freeSlotDeliveryCostFrom - total),
                    })
                    : tca('delivery.free')}
                </span>
                <ReactSVG
                  className={styles.control}
                  src="/icons/arrow-right.svg"
                />
                {showDeliveryList && (
                  <div className={styles['root__delivery-list']}>
                    <p>
                      {tca('delivery.conditions.under_min', {
                        minOrderCost: formatPrice(minOrderCost),
                        deliveryCost: formatPrice(deliveryCost),
                      })}
                    </p>
                    <p>
                      {tca('delivery.conditions.over_free', {
                        freeSlotDeliveryCostFrom: formatPrice(
                          freeSlotDeliveryCostFrom,
                        ),
                      })}
                    </p>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </Wrapper>
  )
}
